<template>
  <div class="change-password-section">
    <section class="change-password-card p-3 mx-auto">
      <div class="change-password-card__header rounded-sm">
        <h5 class="m-0">Cambio de contraseña</h5><i class="fad fa-key"></i>
        <b-button
          class="close-change-password-btn"
          variant="outline-secondary"
          title="Volver al perfil"
          @click="$emit('close-change-password')">
          <i class="fas fa-user-circle"></i>
        </b-button>
      </div>

      <div class="px-4 py-2 small">
        <p class="m-0">Información del Dominio</p>
        <p class="m-0">Nombre: <span class="font-weight-bold">{{ form.domain.name }} ({{ form.domain.id }})</span></p>
        <p class="m-0">Nivel de seguridad: <span class="font-weight-bold">{{ domainSecurityLevelTranslation }}</span></p>
      </div>
      <b-form class="px-4 py-2">
        <b-form-group
          id="current-password-label"
          class="password-input"
          label="Contraseña actual"
          label-for="current-password-input">
          <b-form-input
            id="current-password-input"
            placeholder="Contraseña actual"
            v-model.trim="currentPassword"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="off"
            @focus="clearChangePasswordError"
            lazy
            :disabled="changingPassword">
          </b-form-input>
          <span class="show-password-icon"
            @click="toggleShowPassword('currentPassword')">
            <i v-if="!showPassword" class="fas fa-eye"></i>
            <i v-else class="fas fa-eye-slash"></i>
          </span>
        </b-form-group>

        <b-form-group
          id="new-password-label"
          class="password-input"
          label="Nueva contraseña"
          label-for="new-password-input">
          <b-form-input
            :class="{ 'is-invalid': invalidNewPassword }"
            id="new-password-input"
            placeholder="Nueva contraseña"
            v-model="newPassword"
            :type="showNewPassword ? 'text' : 'password'"
            lazy
            autocomplete="off"
            :disabled="changingPassword">
          </b-form-input>
          <span class="show-password-icon"
            @click="toggleShowPassword('newPassword')">
            <i v-if="!showNewPassword" class="fas fa-eye"></i>
            <i v-else class="fas fa-eye-slash"></i>
          </span>
          <small class="text-danger" v-show="invalidNewPassword">
            <p v-if="!validateNewDifferentPassword" class="m-0">La nueva contraseña debe ser diferente a la contraseña actual.</p>
            <p v-else-if="!validateNewPassword" class="m-0">La nueva contraseña no cumple con los requisitos mínimos.</p>
          </small>
        </b-form-group>

        <b-form-group
          id="new-password-confirmation-label"
          class="password-input"
          label="Confirmar nueva contraseña"
          label-for="new-password-confirmation-input">
          <b-form-input
            :class="{ 'is-invalid': invalidPasswordConfirmation }"
            id="new-password-confirmation-input"
            placeholder="Confirmar nueva contraseña"
            v-model="newPasswordConfirmation"
            :type="showNewPasswordConfirmation ? 'text' : 'password'"
            lazy
            autocomplete="off"
            :disabled="changingPassword">
          </b-form-input>
          <span class="show-password-icon"
            @click="toggleShowPassword('newPasswordConfirmation')">
            <i v-if="!showNewPasswordConfirmation" class="fas fa-eye"></i>
            <i v-else class="fas fa-eye-slash"></i>
          </span>
          <small v-show="invalidPasswordConfirmation" class="text-danger" id="new-password-input-feedback">
            La nueva contraseña y la contraseña de confirmación no coinciden.
          </small>
        </b-form-group>

        <password-details
          description="Tu contraseña debe cumplir lo siguiente:"
          :domain-security-level="domainSecurityLevel" />

        <b-row class="m-0" v-if="!successMessage">
          <b-button class="change-password-button mr-2" variant="outline-secondary"
            @click="$emit('close-change-password')">
            Cancelar
          </b-button>
          <b-button class="change-password-button" variant="primary"
            @click="showChangePasswordModal"
            :disabled="!validateNewDifferentPassword || !validateNewPassword || !validateNewPasswordAndConfirmation">
            Actualizar contraseña
          </b-button>
        </b-row>
      </b-form>
      <b-alert
        v-model="errorOnChangePassword"
        variant="danger" dismissible>
        {{errorOnChangePasswordDescription}}
      </b-alert>
      <b-alert
        class="password-changed-message mt-2 mb-0"
        :show="successMessage"
        variant="success">
        <strong>Contraseña actualizada</strong>
        <p>Tu contraseña ha sido actualizada correctamente. Tu sesión ha sido cerrada.</p>
        <small>Redireccionamiento al inicio de sesión en <i class="far fa-stopwatch"></i> {{ counterToLogout }} seg </small>
      </b-alert>
    </section>
    <b-modal id="change-password-confirmation"
      title="Cambio de contraseña">
        <p>Al continuar con el cambio de contraseña se realizará un cierre de sesión automático y posteriormente un redireccionamiento al inicio de sesión.</p>
        <p>¿estás seguro de continuar?</p>
        <template v-slot:modal-footer>
          <button class="btn btn-light" type="button" @click="hideChangePasswordModal">Cancelar</button>
          <button class="btn btn-primary" type="button" autofocus @click="changePassword">Sí, continuar</button>
        </template>
    </b-modal>
  </div>
</template>

<script lang="ts">
import api, { sendLogoutMessage } from '@/api'
import AuthService from '@/services/support/AuthService'
import CookiesService from '@/services/support/CookiesService'
import userInfoStore from '@/store/userInfo'
import { UserInfo } from '@/types/Auth'
import { DomainSecurityLevel } from '@/types/Catalogs'
import appConfig from '@/config'
import { TOKEN_TYPE_HINT } from '@/types/Login'
import { PASSWORD_REGEXP } from '@/types/RegExpressions'
import Vue from 'vue'
import Component from 'vue-class-component'
import { APIErrorFormat } from '@/types/Errors'
import PasswordDetails from '../generic/PasswordDetails.vue'

@Component({
  name: 'ChangePassword',
  components: {
    PasswordDetails
  }
})
class ChangePassword extends Vue {
  LOGOUT_IN = 10 // seconds
  counterToLogout = 0
  form: UserInfo
  showPasswordRequirements: boolean = false
  currentPassword: string = ''
  newPassword: string = ''
  newPasswordConfirmation: string = ''
  showPassword: boolean = false
  showNewPassword: boolean = false
  showNewPasswordConfirmation: boolean = false
  successMessage: boolean = false
  errorOnChangePassword: boolean = false
  errorOnChangePasswordDescription: string = ''
  changingPassword = false
  domainLevels = DomainSecurityLevel

  created () {
    if (userInfoStore.userInfo.domain.securityLevel === undefined) {
      userInfoStore.userInfo.domain.securityLevel = DomainSecurityLevel.HIGH
    }
    this.form = userInfoStore.userInfo
  }

  /**
   * Returns the domain security level to work with
   */
  get domainSecurityLevel () {
    return this.form.domain.securityLevel
  }

  /**
   * Domain security level label
   */
  get domainSecurityLevelTranslation () {
    switch (this.domainSecurityLevel) {
      case DomainSecurityLevel.LOW:
        return 'Bajo'
      case DomainSecurityLevel.MEDIUM:
        return 'Medio'
      case DomainSecurityLevel.HIGH:
        return 'Alto'
      default:
        return 'Alto'
    }
  }

  /**
   * Manages the flag to show or hide the password value
   *
   * @param password input password flag to update
   */
  toggleShowPassword (password: 'currentPassword' | 'newPassword' | 'newPasswordConfirmation') {
    if (password === 'currentPassword') {
      this.showPassword = !this.showNewPassword
    } else if (password === 'newPassword') {
      this.showNewPassword = !this.showNewPassword
    } else if (password === 'newPasswordConfirmation') {
      this.showNewPasswordConfirmation = !this.showNewPasswordConfirmation
    }
  }

  /** @returns if the input for the current password is empty */
  get isCurrentPasswordEmpty (): boolean {
    return this.currentPassword === ''
  }

  /** @returns if the input for the new password is empty */
  get isNewPasswordEmpty (): boolean {
    return this.newPassword === ''
  }

  /** @returns if the input for the confirmation of the new password is empty */
  get isConfirmationPasswordEmpty (): boolean {
    return this.newPasswordConfirmation === ''
  }

  /**
   * Indicates if the new password input is invalid, this is
   * if it has some caracter but it doesn't meet the requirements of the security level
   * or it isn't different from the current password input
   *
   * @return if the new password is invalid
   */
  get invalidNewPassword () : boolean {
    return !this.isNewPasswordEmpty && (!this.validateNewPassword || !this.validateNewDifferentPassword)
  }

  /**
   * Indicates if the confirmation of the new password is invalid due to
   * this isn't empty and doesn't match with the new password input
   *
   * @return if the confirmation of the new password is invalid
   */
  get invalidPasswordConfirmation () : boolean {
    return !this.isConfirmationPasswordEmpty && !this.validateNewPasswordAndConfirmation
  }

  /**
   * Validates that the user has confirmed correctly the new password
   */
  get validateNewPasswordAndConfirmation () {
    if (this.isNewPasswordEmpty && this.isConfirmationPasswordEmpty) return false
    return this.newPassword === this.newPasswordConfirmation
  }

  /**
   * Validates that the current password is different from the new password
   */
  get validateNewDifferentPassword () {
    if (this.isCurrentPasswordEmpty || this.isNewPasswordEmpty) return false
    return this.currentPassword !== this.newPassword
  }

  /**
   * Validates that the new password satify the requirements
   * based on the domain security level
   */
  get validateNewPassword () {
    if (this.newPassword && this.newPassword.length > 0) {
      let domainSecurityLevelPattern
      switch (this.domainSecurityLevel) {
        case DomainSecurityLevel.LOW:
          domainSecurityLevelPattern = PASSWORD_REGEXP.low_password
          break
        case DomainSecurityLevel.MEDIUM:
          domainSecurityLevelPattern = PASSWORD_REGEXP.medium_password
          break
        case DomainSecurityLevel.HIGH:
          domainSecurityLevelPattern = PASSWORD_REGEXP.high_password
          break
        default:
          domainSecurityLevelPattern = PASSWORD_REGEXP.high_password
          break
      }
      const domainSecurityLevelRegExp = new RegExp(domainSecurityLevelPattern)
      return domainSecurityLevelRegExp.test(this.newPassword)
    }
    return false
  }

  /**
   * Revokes the JWT tokens
   */
  revokeTokenValues () {
    const accessToken = CookiesService.get(appConfig.jwtAccessTokenCookieName)
    if (accessToken) {
      AuthService.revokeToken(accessToken, TOKEN_TYPE_HINT.ACCESS_TOKEN)
    }
    const refreshToken = CookiesService.get(appConfig.refreshTokenCookieName)
    if (refreshToken) {
      AuthService.revokeToken(refreshToken, TOKEN_TYPE_HINT.REFRESH_TOKEN)
    }
  }

  async showChangePasswordModal () {
    this.$bvModal.show('change-password-confirmation')
  }

  async hideChangePasswordModal () {
    this.$bvModal.hide('change-password-confirmation')
  }

  /**
   * Calls the change password ennpoint, close the login
   * and redirect to the login view
   */
  async changePassword () {
    this.changingPassword = true
    this.hideChangePasswordModal()
    let url = `${appConfig.apiUsersUrl}me/change-password`
    let configUrl = {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }
    let dataUrl = `old_password=${this.currentPassword}&new_password=${this.newPassword}`
    await api.post(url, dataUrl, configUrl)
      .then(() => {
        this.successMessage = true
        this.counterStart(this.LOGOUT_IN, (t:number) => { this.counterToLogout = t })
      })
      .then(() => {
        this.showPasswordRequirements = false
        let msg = document.getElementsByClassName('password-changed-message').item(0) as HTMLElement
        msg.focus()

        this.clearChangePasswordError()
        setTimeout(() => {
          sendLogoutMessage()
          this.successMessage = false
        }, 10000)
      })
      .catch((error) => {
        this.errorOnChangePassword = true
        const apiError = APIErrorFormat.getInstance(error.response.data)
        const errorValue = apiError.errors[0]
        if (errorValue.code === 3241) {
          this.errorOnChangePasswordDescription = 'La contraseña actual es incorrecta.'
        } else {
          this.errorOnChangePasswordDescription = errorValue.description
        }
        this.changingPassword = false
      })
  }

  /**
   * Resets the errors info
   */
  clearChangePasswordError () {
    this.errorOnChangePassword = false
    this.errorOnChangePasswordDescription = ''
  }

  /**
   * Timer for manages ranges of N seconds
   *
   * @param counter N seconds for timer
   * @param setCounter function that updates the counter value
   */
  counterStart (counter: number, setCounter: any) {
    setCounter(counter)
    setTimeout(() => {
      if (counter > 0) {
        this.counterStart(counter - 1, setCounter)
      }
    }, 1000)
  }
}
export default ChangePassword
</script>

<style scoped>
.change-password-section {
  width: 100%;
  height: 100%;
}
.change-password-card {
  position: relative;
  max-width: 32rem;
  min-width: 300px;
}
.change-password-card__header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: #e9ecef;
  padding: 1rem;
  position: sticky;
  top: 0;
  z-index: 5;
}
.domain-info__details {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.password-requirements {
  border-radius: 1rem;
  margin-bottom: 0.75rem;
  font-size: 0.75rem;
  z-index: 0;
}
.password-requirements.low {
  border: 4px solid #ffc10780;
}
.password-requirements.medium {
  border: 4px solid #fd7e1480;
}
.password-requirements.high {
  border: 4px solid #dc354580;
}
::v-deep .password-input > div {
  position: relative;
}
.show-password-icon {
  position: absolute;
  top: 0;
  right: 0;
  margin: 0.5rem;
}
.password-input > .was-validated .form-control:invalid, .form-control.is-invalid {
  background-image: none;
}
.password-input > .was-validated .form-control:invalid, .form-control.is-valid {
  background-image: none;
}
.change-password-button {
  width: 49%;
}
.close-change-password-btn {
  position: absolute;
  top: 0;
  left: -4rem;
  margin: 0.5rem;
  border-radius: 0.75rem;
}
</style>
